import { Extension, Range, Editor as CoreEditor } from '@tiptap/core'
import Suggestion, { SuggestionMatch, Trigger } from '@tiptap/suggestion'
import { Editor, VueRenderer } from '@tiptap/vue-2'
import tippy, { Instance, Props } from 'tippy.js'
import VariableList from '@/modules/global/components/VariableList.vue'
import VariableModal from '@/modules/global/components/VariableModal.vue'
import { mapApiTypeToUI } from '@/util/variable-type-utils'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { TextSelection } from '@tiptap/pm/state'
import { EditorView } from '@tiptap/pm/view'
import Vue from 'vue'
import { useVariableTypeStore } from '@/modules/global/stores/VariableTypeStore'
import { useSpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { useAppStore } from '@/modules/global/stores/AppStore'
import { usePermissionStore } from '@/modules/auth/stores/PermissionStore'
import SpaceVariable from '@/modules/space/models/SpaceVariable'
import { buildAssignCalloutAttributes, getAvatarColorIndex, getAvatarInitial } from '@/util/variable-callout-utils'
import { getVariableIconState, getVariableValueFlags } from '@/util/variable-state'
import {
  deleteAllPlaceholderInstances,
  deleteAllPlaceholderInstancesFromAllEditors,
  findAllPlaceholderInstances
} from '@/util/tiptap-variable-placeholder'
import {
  handleVariableDeleteApiError,
  isVariableSchemaLocked,
  promptUnlockIfVariableSchemaLocked
} from '@/util/variable-schema-lock'

export const variableSuggestionPluginKey = new PluginKey('variableSuggestion')

type TiptapEditor = Editor | CoreEditor

const VARIABLE_NAME_ALLOWED_REGEX = /^[A-Za-z0-9_]+$/
const VARIABLE_NAME_INVALID_REGEX = /[^A-Za-z0-9_]/g
const VARIABLE_OPEN_BRACE = '{{'
const VARIABLE_CLOSE_BRACE = '}}'
const VARIABLE_SEARCH_BACKWARD_LIMIT = 10
const VARIABLE_SEARCH_FORWARD_LIMIT = 10

function sanitizeVariableName(value: string): string {
  return value.replace(VARIABLE_NAME_INVALID_REGEX, '')
}

function isValidVariableName(value: string): boolean {
  return !!value && VARIABLE_NAME_ALLOWED_REGEX.test(value)
}

function isAllowedVariableChar(value: string): boolean {
  return value.length === 1 && VARIABLE_NAME_ALLOWED_REGEX.test(value)
}

function findVariablePositions(range: Range, editor: TiptapEditor): { openPos: number; closePos: number } | null {
  const docSize = editor.state.doc.content.size
  let openPos = -1

  // Search backwards for opening brace from range start
  const startSearch = Math.max(0, range.from - VARIABLE_SEARCH_BACKWARD_LIMIT)
  for (let i = range.from; i >= startSearch; i--) {
    if (i < 2) break
    const text = editor.state.doc.textBetween(i - 2, i)
    if (text === VARIABLE_OPEN_BRACE) {
      openPos = i - 2
      break
    }
  }

  if (openPos === -1) {
    return null
  }

  // Search forwards for closing brace starting from after opening brace
  let closePos = -1
  const searchStart = openPos + 2
  const endSearch = Math.min(docSize, searchStart + VARIABLE_SEARCH_FORWARD_LIMIT)

  // First, check if range.to already points to a closing brace (common case)
  if (range.to >= searchStart && range.to <= docSize) {
    // Check range.to to range.to + 2 (for when cursor is exactly at }})
    if (range.to + 2 <= docSize) {
      const textAtRangeEnd = editor.state.doc.textBetween(range.to, range.to + 2)
      if (textAtRangeEnd === VARIABLE_CLOSE_BRACE) {
        closePos = range.to + 2
      }
    }

    // Check range.to + 1 to range.to + 3 (for when cursor is at space before }})
    if (closePos === -1 && range.to + 3 <= docSize) {
      const textAfterRangeEnd = editor.state.doc.textBetween(range.to + 1, range.to + 3)
      if (textAfterRangeEnd === VARIABLE_CLOSE_BRACE) {
        closePos = range.to + 3
      }
    }

    // Check range.to - 2 to range.to (for when cursor is after }})
    if (closePos === -1 && range.to >= 2) {
      const textBeforeRangeEnd = editor.state.doc.textBetween(range.to - 2, range.to)
      if (textBeforeRangeEnd === VARIABLE_CLOSE_BRACE) {
        closePos = range.to
      }
    }
  }

  // If not found at range.to, search forward from after opening brace
  if (closePos === -1) {
    for (let i = searchStart; i <= endSearch; i++) {
      if (i + 2 > docSize) break
      const text = editor.state.doc.textBetween(i, i + 2)
      if (text === VARIABLE_CLOSE_BRACE) {
        closePos = i + 2
        break
      }
    }
  }

  // Must find closing brace and ensure it's after opening brace
  if (closePos === -1 || closePos <= openPos) {
    return null
  }

  return { openPos, closePos }
}

/**
 * Creates ProseMirror nodes for a variable with proper attributes and structure
 * @param schema - The ProseMirror schema
 * @param variableText - The formatted variable text (e.g., "{{ variableName }}")
 * @param hasDefault - Whether the variable has a default value
 * @param hasRecipient - Whether the variable has a recipient assignment
 * @param variableId - Optional variable ID from SpaceVariable
 * @param variableName - Variable name
 * @param recipientEmail - Optional recipient email/name to display in callout
 * @param variable - Optional SpaceVariable object for icon determination
 * @returns Object containing the variableWrapperNode and caretAnchorNode
 */
function createVariableNodes(
  schema: any,
  variableText: string,
  hasDefault: boolean,
  hasRecipient: boolean,
  variableId?: string,
  variableName?: string,
  recipientEmail?: string,
  variable?: SpaceVariable,
  allowAssignCallout: boolean = true
) {
  const iconState = variable ? getVariableIconState(variable) : null

  // Build mark attributes
  const markAttributes: Record<string, string | null> = {
    'data-has-default': hasDefault ? 'true' : 'false'
  }

  if (hasRecipient) {
    markAttributes['data-has-recipient'] = 'true'
  }
  if (variableId) {
    markAttributes['data-variable-id'] = variableId
  }
  if (variableName) {
    markAttributes['data-variable-name'] = variableName
  }
  if (iconState) {
    markAttributes['data-icon-state'] = iconState
  }
  if (iconState === 'pend') {
    if (recipientEmail) {
      markAttributes['data-avatar-letter'] = getAvatarInitial(undefined, recipientEmail)
      markAttributes['data-avatar-color-index'] = getAvatarColorIndex(recipientEmail).toString()
    }
  }

  // Create nodes
  const variablePlaceholderMark = schema.marks.variablePlaceholder.create(markAttributes)
  const variableTextNode = schema.text(variableText, [variablePlaceholderMark])
  const wrapperContent = [variableTextNode]

  // Add assign-callout for all variables (shown on hover)
  if (allowAssignCallout) {
    wrapperContent.push(schema.nodes.assignCallout.create(buildAssignCalloutAttributes(variable, recipientEmail)))
  }

  return {
    variableWrapperNode: schema.nodes.variableWrapper.create({}, wrapperContent),
    caretAnchorNode: schema.nodes.caretAnchor.create()
  }
}

// Cache Vue.extend result for better performance - compiled once
const VariableModalClass = Vue.extend(VariableModal)

/**
 * Custom findSuggestionMatch to detect when cursor is inside {{ }}
 * Only activates if the text is inside a variablePlaceholder mark or if creating a new variable
 */
function findVariableSuggestionMatch(config: Trigger): SuggestionMatch {
  const { $position } = config
  const pos = $position.pos
  const docSize = $position.doc.content.size

  // Early return if position is invalid
  if (pos < 0 || pos > docSize) {
    return null
  }

  // Search backwards for opening {{ with limited range for performance
  let openPos = -1
  const searchBackStart = Math.max(0, pos - 200) // Reduced from 500
  for (let i = pos; i >= searchBackStart; i--) {
    if (i < 2) break
    const text = $position.doc.textBetween(i - 2, i)
    if (text === VARIABLE_OPEN_BRACE) {
      openPos = i - 2
      break
    }
  }

  if (openPos === -1) {
    return null
  }

  // Search forwards for closing }} starting from right after the opening brace
  // This ensures we find the closing brace that belongs to the opening brace we found
  let closePos = -1
  const searchStart = openPos + 2
  const searchEnd = Math.min(docSize, searchStart + 200) // Reduced from 500

  for (let i = searchStart; i < searchEnd; i++) {
    if (i + 2 > docSize) break
    const text = $position.doc.textBetween(i, i + 2)
    if (text === VARIABLE_CLOSE_BRACE) {
      closePos = i + 2
      break
    }
  }

  if (closePos === -1 || openPos >= closePos || pos <= openPos || pos >= closePos) {
    return null
  }

  // Get content between braces
  const contentStart = openPos + 2
  const contentEnd = closePos - 2
  const contentText = $position.doc.textBetween(contentStart, contentEnd)
  const query = contentText.trim()

  // Check if any position in the range has the variablePlaceholder mark
  // Optimize: check most likely positions first (current pos, then around braces)
  let hasVariablePlaceholderMark = false
  const markCheckPositions = [
    pos, // Most likely position
    openPos + 2, // Right after opening brace
    closePos - 2, // Right before closing brace
    openPos, // At opening brace
    closePos // At closing brace
  ]

  for (const checkPos of markCheckPositions) {
    if (checkPos >= 0 && checkPos < docSize) {
      if (
        $position.doc
          .resolve(checkPos)
          .marks()
          .some(mark => mark.type.name === 'variablePlaceholder')
      ) {
        hasVariablePlaceholderMark = true
        break
      }
    }
  }

  // Only activate if:
  // 1. Inside a variablePlaceholder mark (editing existing variable), OR
  // 2. Content is empty or just spaces (creating new variable by typing {{)
  const isEmptyOrWhitespace = !query || /^\s*$/.test(contentText)

  if (!hasVariablePlaceholderMark && !isEmptyOrWhitespace) {
    // This is plain text like {{ Not a Variable }}, don't activate suggestion
    return null
  }

  // Calculate actual content range (skip leading/trailing spaces)
  // Optimize: use single pass to find both start and end
  let actualStart = contentStart
  let actualEnd = contentEnd

  // Find start (skip leading spaces)
  for (let i = contentStart; i < contentEnd; i++) {
    const char = $position.doc.textBetween(i, i + 1)
    if (char !== ' ' && char !== '\n' && char !== '\t') {
      actualStart = i
      break
    }
  }

  // Find end (skip trailing spaces) - only if we haven't reached the end
  if (actualStart < contentEnd) {
    for (let i = contentEnd - 1; i >= actualStart; i--) {
      const char = $position.doc.textBetween(i, i + 1)
      if (char !== ' ' && char !== '\n' && char !== '\t') {
        actualEnd = i + 1
        break
      }
    }
  }

  // If cursor is in the content area, activate suggestion
  if (pos >= actualStart && pos <= actualEnd) {
    return {
      range: {
        from: actualStart,
        to: actualEnd
      },
      query: query,
      text: $position.doc.textBetween(openPos, closePos)
    }
  }

  return null
}

export const VariableSuggestion = Extension.create({
  name: 'variableSuggestion',

  addOptions() {
    return {
      parent: null as Vue | null,
      scopeId: null as string | null,
      suggestion: {
        char: '',
        startOfLine: false,
        allowedPrefixes: null,
        allowSpaces: false,
        command: () => {}
      }
    }
  },

  addProseMirrorPlugins() {
    const parent = this.options.parent
    const getScopeId = () => this.options.scopeId || useSpaceStore().currentSpaceId
    const editor = this.editor

    const renderWithParent = () => {
      let component: VueRenderer
      let popup: Instance<Props>[]
      let range: Range | null = null
      let pendingNewVariable: { name: string; range: Range; openPos: number; closePos: number } | null = null
      let modalComponent: Vue | null = null
      let modalElement: HTMLElement | null = null
      let editorEditableBeforeModal: boolean | null = null
      let lastKeyPressed: string | null = null
      let isInserting: boolean = false
      let isProgrammaticHide: boolean = false
      let clickedVariable: string | null = null
      let popupJustDestroyed: boolean = false
      let popupDestroyTimeout: ReturnType<typeof setTimeout> | null = null

      const findVariableWrapperAtPos = (editor: Editor, pos: number) => {
        const { doc } = editor.state
        const safePos = Math.max(0, Math.min(pos, doc.content.size))
        const $pos = doc.resolve(safePos)
        for (let depth = $pos.depth; depth > 0; depth--) {
          const node = $pos.node(depth)
          if (node.type.name === 'variableWrapper') {
            const start = $pos.before(depth)
            const end = start + node.nodeSize
            return { node, start, end }
          }
        }
        return null
      }

      const isTemplateContext = () => {
        const scopeId = this.options.scopeId
        return !!scopeId && scopeId !== useSpaceStore().currentSpaceId
      }

      const insertVariable = (variable: string, openPos: number, closePos: number, editor: Editor) => {
        // Validate positions before proceeding
        const docSize = editor.state.doc.content.size
        if (openPos < 0 || closePos <= openPos || closePos > docSize) {
          return
        }

        // Verify the range contains braces to avoid deleting wrong content
        const textToDelete = editor.state.doc.textBetween(openPos, closePos)
        if (!textToDelete.includes('{{') || !textToDelete.includes('}}')) {
          return
        }

        // Set flag to prevent onUpdate from recreating popup during insertion
        isInserting = true

        const variableText = `{{ ${variable} }}`

        // Look up variable in store to get ID and check for default/assignment
        const spaceVariableStore = useSpaceVariableStore()
        const spaceId = getScopeId()
        let variableId: string | undefined
        let hasDefault = false
        let hasRecipient = false
        let recipientEmail: string | undefined
        let variableObj: SpaceVariable | undefined

        if (spaceId) {
          variableObj = spaceVariableStore.getVariableByName(spaceId, variable)
          if (variableObj) {
            variableId = variableObj.id
            const flags = getVariableValueFlags(variableObj)
            hasDefault = flags.hasDefault
            hasRecipient = flags.hasRecipient
            recipientEmail = variableObj.assignedToRecipientName?.trim() || undefined
          }
        }

        const allowAssignCallout = !isTemplateContext()
        const wrapperInfo = findVariableWrapperAtPos(editor, openPos + 1)
        if (wrapperInfo) {
          const wrapperText = wrapperInfo.node.textBetween(0, wrapperInfo.node.content.size)
          const existingMatch = wrapperText.match(/\{\{\s*([^}]+?)\s*\}\}/)
          const existingName = existingMatch?.[1]?.trim()
          // The wrapper only counts as this variable once its mark carries the variable's id: a placeholder whose
          // typed text merely matches the new name (an empty {{ }} chip filled in, then created) must be replaced
          let isResolved = !variableId
          wrapperInfo.node.forEach(child => {
            const mark = child.marks.find(m => m.type.name === 'variablePlaceholder')
            if (mark && mark.attrs['data-variable-id'] === variableId) isResolved = true
          })
          if (isResolved && existingName && existingName.toLowerCase() === variable.toLowerCase()) {
            const { state, view } = editor
            let cursorPos = wrapperInfo.end
            const nodeAfter = state.doc.nodeAt(cursorPos)
            if (nodeAfter?.type.name === 'caretAnchor') {
              cursorPos += nodeAfter.nodeSize
            }
            view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, cursorPos)))
            return
          }

          // Replace entire wrapper (and caret anchor if present) instead of nesting
          isInserting = true
          const { state, view } = editor
          const { schema } = state
          const { variableWrapperNode, caretAnchorNode } = createVariableNodes(
            schema,
            variableText,
            hasDefault,
            hasRecipient,
            variableId,
            variable,
            recipientEmail,
            variableObj,
            allowAssignCallout
          )

          const replaceFrom = wrapperInfo.start
          let replaceTo = wrapperInfo.end
          const nodeAfter = state.doc.nodeAt(replaceTo)
          if (nodeAfter?.type.name === 'caretAnchor') {
            replaceTo += nodeAfter.nodeSize
          }

          const tr = state.tr.replaceWith(replaceFrom, replaceTo, variableWrapperNode)
          const insertPos = replaceFrom + variableWrapperNode.nodeSize
          tr.insert(insertPos, caretAnchorNode)

          const cursorPosAfter = Math.min(insertPos + caretAnchorNode.nodeSize, tr.doc.content.size)
          tr.setSelection(TextSelection.create(tr.doc, cursorPosAfter))
          view.dispatch(tr)

          requestAnimationFrame(() => {
            isInserting = false
          })
          return
        }

        // Delete the range first - this will cause suggestion to exit
        editor.chain().focus().deleteRange({ from: openPos, to: closePos }).run()

        // Wait for suggestion to exit, then insert variable using ProseMirror transaction
        requestAnimationFrame(() => {
          const { state, view } = editor
          const { schema, selection } = state
          const insertPos = selection.from

          // Create variable nodes with proper attributes including variable ID, name, recipient email, and variable object for icon
          const { variableWrapperNode, caretAnchorNode } = createVariableNodes(
            schema,
            variableText,
            hasDefault,
            hasRecipient,
            variableId,
            variable,
            recipientEmail,
            variableObj,
            allowAssignCallout
          )

          // Insert: variable-wrapper, then caret-anchor
          const nodesToInsert = [variableWrapperNode, caretAnchorNode]

          // Create transaction to insert nodes
          const tr = state.tr
          tr.insert(insertPos, nodesToInsert)

          // Calculate cursor position after inserted nodes
          const totalNodeSize = variableWrapperNode.nodeSize + caretAnchorNode.nodeSize
          const cursorPosAfterInsertion = insertPos + totalNodeSize

          // Set cursor position after the inserted variable
          tr.setSelection(TextSelection.create(tr.doc, cursorPosAfterInsertion))

          // Apply transaction
          view.dispatch(tr)

          // Immediately sync DOM selection with ProseMirror selection to prevent visual glitch
          // This is especially important when insertPos is 0 or 1 (start of document) and when clicking
          // The click event can cause DOM selection to briefly show at the start of the variable wrapper
          if (insertPos <= 1) {
            // Clear any DOM selection immediately to prevent visual glitch
            const domSelection = window.getSelection()
            if (domSelection) {
              domSelection.removeAllRanges()
            }

            // Use microtask for immediate DOM sync (faster than requestAnimationFrame)
            // This ensures cursor appears in correct position immediately, preventing visual glitch
            Promise.resolve().then(() => {
              // Ensure ProseMirror's selection is properly reflected in DOM
              const { state: syncState } = editor
              if (syncState.selection.from !== cursorPosAfterInsertion) {
                editor.chain().focus().setTextSelection(cursorPosAfterInsertion).run()
              }

              // Also explicitly set DOM selection to match ProseMirror
              const domAtPos = view.domAtPos(cursorPosAfterInsertion)
              if (domAtPos && domAtPos.node) {
                const range = document.createRange()
                range.setStart(domAtPos.node, domAtPos.offset)
                range.setEnd(domAtPos.node, domAtPos.offset)
                const syncDomSelection = window.getSelection()
                if (syncDomSelection) {
                  syncDomSelection.removeAllRanges()
                  syncDomSelection.addRange(range)
                }
              }
            })

            // Also use requestAnimationFrame as backup to ensure it sticks
            view.dom.ownerDocument.defaultView?.requestAnimationFrame(() => {
              const { state: rafState } = editor
              if (rafState.selection.from !== cursorPosAfterInsertion) {
                editor.chain().focus().setTextSelection(cursorPosAfterInsertion).run()
              }
            })
          }

          // Explicitly set cursor position after transaction to handle cases where:
          // 1. Click event causes popup destruction and focus loss
          // 2. When insertPos is 0 (start of document), browser/ProseMirror may default cursor to position 0
          // For insertPos 0 or 1, ensure cursor is positioned correctly after insertion
          // This handles cases where clicking (vs Enter) can cause cursor to appear at start
          if (insertPos <= 1) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  const { state: currentState } = editor
                  const currentDocSize = currentState.doc.content.size

                  if (cursorPosAfterInsertion > 0 && cursorPosAfterInsertion <= currentDocSize) {
                    // Find the actual end of the variable wrapper node
                    let actualCursorPos = cursorPosAfterInsertion
                    let wrapperEndPos = -1

                    currentState.doc.nodesBetween(
                      Math.max(0, insertPos - 2),
                      Math.min(currentDocSize, insertPos + 30),
                      (node, pos) => {
                        if (node.type.name === 'variableWrapper') {
                          wrapperEndPos = pos + node.nodeSize
                          // Check for caret-anchor node after wrapper
                          if (wrapperEndPos < currentDocSize) {
                            const nodeAfter = currentState.doc.nodeAt(wrapperEndPos)
                            if (nodeAfter && nodeAfter.type.name === 'caretAnchor') {
                              wrapperEndPos = wrapperEndPos + nodeAfter.nodeSize
                            }
                          }
                          return false
                        }
                      }
                    )

                    if (wrapperEndPos > 0) {
                      actualCursorPos = wrapperEndPos
                    }

                    // Set cursor position
                    const currentCursorPos = currentState.selection.from
                    if (currentCursorPos !== actualCursorPos) {
                      editor.chain().focus().setTextSelection(actualCursorPos).run()
                    }
                  }
                  isInserting = false
                }, 10)
              })
            })
          } else {
            isInserting = false
          }
        })
      }

      const clearVariableText = (openPos: number, closePos: number, editor: Editor) => {
        const docSize = editor.state.doc.content.size
        if (openPos < 0 || closePos <= openPos || closePos > docSize) {
          return
        }

        const textToDelete = editor.state.doc.textBetween(openPos, closePos)
        if (!textToDelete.includes('{{') || !textToDelete.includes('}}')) {
          return
        }

        isInserting = true
        const emptyPlaceholder = '{{  }}'

        editor.chain().focus().deleteRange({ from: openPos, to: closePos }).run()

        requestAnimationFrame(() => {
          const { state } = editor
          const insertPos = state.selection.from

          editor.chain().insertContentAt(insertPos, emptyPlaceholder).run()

          const { doc, selection } = editor.state
          const afterInsertPos = selection.from

          let variableStart = afterInsertPos
          const searchStart = Math.max(0, afterInsertPos - 50)
          for (let i = afterInsertPos; i >= searchStart; i--) {
            const checkStart = Math.max(0, i - 3)
            if (doc.textBetween(checkStart, i) === '{{ ') {
              variableStart = checkStart
              break
            }
          }

          const variableEnd = variableStart + emptyPlaceholder.length

          editor
            .chain()
            .setTextSelection({ from: variableStart, to: variableEnd })
            .setMark('variablePlaceholder')
            .setTextSelection(variableStart + 3)
            .run()

          setTimeout(() => {
            isInserting = false
          }, 0)
        })
      }

      const openVariableCreationModal = (variableName: string, range: Range, editor: Editor) => {
        if (
          !isTemplateContext() &&
          promptUnlockIfVariableSchemaLocked({
            resume: () => openVariableCreationModal(variableName, range, editor)
          })
        ) {
          dismissSuggestionPopup()
          return
        }

        const positions = findVariablePositions(range, editor)
        if (!positions) {
          return
        }

        pendingNewVariable = {
          name: variableName,
          range,
          openPos: positions.openPos,
          closePos: positions.closePos
        }

        // Ensure flag is set before hiding to prevent race conditions
        if (!isProgrammaticHide) {
          isProgrammaticHide = true
        }
        if (popup?.[0]) {
          popup[0].hide()
        }
        destroyPopup()
        if (component) {
          component.destroy()
        }

        cleanupModal()

        if (editorEditableBeforeModal === null) {
          editorEditableBeforeModal = editor.isEditable
          editor.setEditable(false)
        }
        editor.view.dom.blur()

        modalElement = document.createElement('div')
        document.body.appendChild(modalElement)

        modalComponent = new VariableModalClass({
          parent: parent,
          propsData: {
            variableName,
            open: true,
            scopeId: getScopeId()
          }
        })

        modalComponent.$mount(modalElement)

        modalComponent.$on('submit', (createdVariable: SpaceVariable) => {
          if (pendingNewVariable) {
            const variableTypeStore = useVariableTypeStore()
            variableTypeStore.setVariableMetadata(
              createdVariable.variableName,
              mapApiTypeToUI(createdVariable.variableType),
              createdVariable.defaultValue,
              createdVariable.dateFormat
            )
            insertVariable(
              createdVariable.variableName,
              pendingNewVariable.openPos,
              pendingNewVariable.closePos,
              editor
            )
            cleanupModal()
            pendingNewVariable = null
          }
        })

        modalComponent.$on('close', () => {
          // Clear variable text if modal is closed without submit
          if (pendingNewVariable) {
            clearVariableText(pendingNewVariable.openPos, pendingNewVariable.closePos, editor)
          }
          cleanupModal()
          pendingNewVariable = null
        })
      }

      const cleanupModal = () => {
        if (modalComponent) {
          modalComponent.$destroy()
          modalComponent = null
        }
        if (modalElement?.parentNode) {
          modalElement.parentNode.removeChild(modalElement)
          modalElement = null
        }
        if (editorEditableBeforeModal !== null) {
          editor.setEditable(editorEditableBeforeModal)
          editorEditableBeforeModal = null
        }
      }

      const openModifyModal = (variable: SpaceVariable, _editor: Editor) => {
        if (
          !isTemplateContext() &&
          promptUnlockIfVariableSchemaLocked({
            resume: () => openModifyModal(variable, _editor)
          })
        ) {
          dismissSuggestionPopup()
          return
        }

        // Hide popup if open
        if (popup?.[0]) {
          popup[0].hide()
        }
        destroyPopup()
        if (component) {
          component.destroy()
        }

        cleanupModal()

        modalElement = document.createElement('div')
        document.body.appendChild(modalElement)

        modalComponent = new VariableModalClass({
          parent: parent,
          propsData: {
            variable,
            open: true,
            scopeId: getScopeId()
          }
        })

        modalComponent.$mount(modalElement)

        modalComponent.$nextTick(() => {
          if (modalComponent) {
            const modalComponentAny = modalComponent as any
            if (modalComponentAny.variable && modalComponentAny.initializeForm) {
              modalComponentAny.initializeForm()
            }
          }
        })

        modalComponent.$on('submit', (_updatedVariable: SpaceVariable) => {
          cleanupModal()
        })

        modalComponent.$on('close', () => {
          cleanupModal()
        })
      }

      const destroyPopup = () => {
        if (popup?.[0]) {
          try {
            isProgrammaticHide = true
            popupJustDestroyed = true
            popup[0].destroy()
          } catch {
            // Ignore errors during cleanup
          }
          popup = []
          isProgrammaticHide = false
          // Clear any existing timeout
          if (popupDestroyTimeout) {
            clearTimeout(popupDestroyTimeout)
          }
          // Reset flag after a short delay to allow onUpdate to check it
          popupDestroyTimeout = setTimeout(() => {
            popupJustDestroyed = false
            popupDestroyTimeout = null
          }, 100)
        }
      }

      /** Tear down the variable suggestion popover so App-level confirms are not obscured. */
      const dismissSuggestionPopup = () => {
        clickedVariable = null
        range = null
        if (popup?.[0]) {
          try {
            popup[0].hide()
          } catch {
            // Ignore errors during cleanup
          }
        }
        destroyPopup()
        if (component) {
          component.destroy()
        }
        isProgrammaticHide = true
        popupJustDestroyed = true
        editor.view.dom.blur()
      }

      const findPlaceholderElement = (
        targetEditor: Editor,
        variable: string,
        variableId?: string
      ): HTMLElement | null => {
        const root = targetEditor.view.dom
        if (variableId) {
          const byId = root.querySelector(`.variable-placeholder[data-variable-id="${variableId}"]`)
          if (byId) {
            return byId as HTMLElement
          }
        }
        const escapedName =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(variable) : variable
        const byName = root.querySelector(`.variable-placeholder[data-variable-name="${escapedName}"]`)
        return (byName as HTMLElement) || null
      }

      const reopenVariableActionMenu = (
        targetEditor: Editor,
        variable: string,
        variableId: string | undefined,
        spaceVariable: SpaceVariable | undefined
      ) => {
        const placeholderEl = findPlaceholderElement(targetEditor, variable, variableId)
        if (!placeholderEl) {
          return
        }

        if (popup?.[0]) {
          try {
            popup[0].hide()
          } catch {
            // Ignore errors during cleanup
          }
        }
        destroyPopup()
        if (component) {
          component.destroy()
        }

        isProgrammaticHide = false
        popupJustDestroyed = false

        try {
          const docPos = targetEditor.view.posAtDOM(placeholderEl, 0)
          range = { from: docPos, to: docPos }
        } catch {
          range = null
        }

        const { spaceVariables } = getVariablesFromStore()
        let filtered = filterVariables(spaceVariables, variable).filter(
          item => item.variable.toLowerCase() === variable.toLowerCase() && !item.isNew
        )

        if (filtered.length === 0 && spaceVariable) {
          filtered = [
            {
              variable: spaceVariable.variableName,
              variableType: mapTypeToDisplay(spaceVariable.variableType),
              variableId: spaceVariable.id,
              spaceVariable
            }
          ]
        }

        if (filtered.length === 0) {
          return
        }

        const items = filtered.map(item => item.variable)
        component = new VueRenderer(VariableList, {
          parent: parent,
          propsData: {
            items,
            query: variable,
            filteredItems: filtered,
            onNewVariable: (variableName: string) => handleNewVariable(variableName, targetEditor),
            onModify: (v: string, vid?: string, sv?: SpaceVariable) => handleModifyVariable(v, vid, sv, targetEditor),
            onDelete: (v: string, vid?: string, sv?: SpaceVariable) => handleDeleteVariable(v, vid, sv, targetEditor),
            onActionMenuOpen: () => {
              isProgrammaticHide = true
            },
            command: buildCommandHandler(filtered, targetEditor)
          }
        })

        const clientRect = () => placeholderEl.getBoundingClientRect()
        popup = createPopup(clientRect, component.element as Element, targetEditor, buildHandlePopupHide(targetEditor))
        clickedVariable = variable
      }

      const shouldOpenModalOnBlur = (_editor: Editor): { shouldOpen: boolean; variableName: string | null } => {
        if (!range || pendingNewVariable || modalComponent || !component?.ref || isInserting) {
          return { shouldOpen: false, variableName: null }
        }

        const queryValue = (component.ref as any)?.query || ''
        const queryTrimmed = queryValue.trim()

        // Early return if query is empty or contains invalid characters
        if (!isValidVariableName(queryTrimmed)) {
          return { shouldOpen: false, variableName: null }
        }

        const queryLower = queryTrimmed.toLowerCase()
        const { variables } = getVariablesFromStore()
        const variableNameSet = new Set(variables.map(v => v.toLowerCase()))

        return variableNameSet.has(queryLower)
          ? { shouldOpen: false, variableName: null }
          : { shouldOpen: true, variableName: queryTrimmed }
      }

      // The suggestion decoration's rect is null or empty when the editor is parked/pooled (table cells), which
      // sends the popup to the page corner; anchor to the caret instead.
      const anchorRect = (clientRect: (() => DOMRect | null) | null | undefined, editor: Editor) => () => {
        const rect = clientRect?.()
        if (rect && (rect.width > 0 || rect.height > 0 || rect.top > 0 || rect.left > 0)) return rect
        try {
          const { left, right, top, bottom } = editor.view.coordsAtPos(editor.state.selection.from)
          return { left, right, top, bottom, width: right - left, height: bottom - top, x: left, y: top } as DOMRect
        } catch {
          return rect ?? new DOMRect(0, 0, 0, 0)
        }
      }

      const createPopup = (clientRect: any, content: Element, editor: Editor, onHide?: () => void) => {
        return tippy('body', {
          getReferenceClientRect: anchorRect(clientRect, editor),
          appendTo: () => document.body,
          content,
          showOnCreate: true,
          interactive: true,
          theme: 'none',
          trigger: 'manual',
          placement: 'bottom-start',
          // Above the NModal backdrop ($notch-layer-overlay: 1000) so the list shows in the task modal and template
          // editor, but below the covering overlay ($notch-layer-covering-overlay: 1002) that App-level confirms use.
          zIndex: 1001,
          onHide: onHide
        })
      }

      const getVariablesFromStore = (): { variables: string[]; spaceVariables: SpaceVariable[] } => {
        const spaceVariableStore = useSpaceVariableStore()
        const spaceId = getScopeId()

        if (!spaceId) {
          return { variables: [], spaceVariables: [] }
        }

        const spaceVariables = spaceVariableStore.getVariablesBySpaceId(spaceId)
        const variables = spaceVariables.map(v => v.variableName)

        return { variables, spaceVariables }
      }

      const buildHandlePopupHide = (editor: Editor) => () => {
        // Only trigger if not programmatic hide and conditions are met
        if (!isProgrammaticHide && !clickedVariable) {
          const { shouldOpen, variableName } = shouldOpenModalOnBlur(editor)
          if (shouldOpen && variableName) {
            openVariableCreationModal(variableName, range!, editor)
          }
        } else {
          // Reset flag after programmatic hide completes
          isProgrammaticHide = false
          clickedVariable = null
        }
      }

      const handleNewVariable = (variableName: string, editor: Editor) => {
        // Mark as programmatic hide immediately to prevent onHide callback from firing
        const trimmedName = variableName.trim()
        clickedVariable = trimmedName
        isProgrammaticHide = true
        if (range && isValidVariableName(trimmedName)) {
          openVariableCreationModal(trimmedName, range, editor)
        }
        // Flag will be reset in handlePopupHide when onHide fires
      }

      const buildCommandHandler =
        (filteredItems: Array<{ variable: string; isNew?: boolean }>, editor: Editor) =>
        ({ variable }: { variable: string }) => {
          // Mark as programmatic hide immediately to prevent onHide callback from firing
          clickedVariable = variable
          isProgrammaticHide = true
          if (!range) {
            isProgrammaticHide = false
            clickedVariable = null
            return
          }

          const variableLower = variable.toLowerCase()
          const filteredItem = filteredItems.find(item => item.variable.toLowerCase() === variableLower)

          if (filteredItem?.isNew) {
            openVariableCreationModal(variable, range, editor)
            // Flag will be reset in handlePopupHide when onHide fires
          } else {
            const positions = findVariablePositions(range, editor)

            if (positions) {
              // Hide and destroy popup immediately to close dropdown
              if (popup?.[0]) {
                popup[0].hide()
              }
              destroyPopup()
              if (component) {
                component.destroy()
              }

              range = null
              insertVariable(variable, positions.openPos, positions.closePos, editor)
              isProgrammaticHide = false
              clickedVariable = null
            } else {
              isProgrammaticHide = false
              clickedVariable = null
            }
          }
        }

      const mapTypeToDisplay = (type: string): string => {
        if (type === 'number') return 'Number'
        if (type === 'email') return 'E-mail'
        return type
      }

      const filterVariables = (
        spaceVariables: SpaceVariable[],
        query: string
      ): Array<{
        variable: string
        variableType?: string
        isNew?: boolean
        variableId?: string
        spaceVariable?: SpaceVariable
      }> => {
        const trimmedQuery = query.trim()
        const lowercaseQuery = trimmedQuery.toLowerCase()

        if (!lowercaseQuery) {
          // Early return for empty query - return all variables sorted
          return spaceVariables
            .map(v => ({
              variable: v.variableName,
              variableType: mapTypeToDisplay(v.variableType),
              variableId: v.id,
              spaceVariable: v
            }))
            .sort((a, b) => a.variable.toLowerCase().localeCompare(b.variable.toLowerCase()))
        }

        const variableNameSet = new Set<string>()
        const results: Array<{
          variable: string
          variableType?: string
          isNew?: boolean
          variableId?: string
          spaceVariable?: SpaceVariable
        }> = []

        // Single pass: build set and filter results
        for (const spaceVar of spaceVariables) {
          const variableLower = spaceVar.variableName.toLowerCase()
          variableNameSet.add(variableLower)

          if (variableLower.includes(lowercaseQuery)) {
            // Existing variables should NEVER show as "new"
            // Only add them if they match the query
            results.push({
              variable: spaceVar.variableName,
              variableType: mapTypeToDisplay(spaceVar.variableType),
              variableId: spaceVar.id,
              spaceVariable: spaceVar,
              isNew: false // Existing variables are never "new"
            })
          }
        }

        // Only add the query itself as "new" if it doesn't exist as an exact match
        if (!variableNameSet.has(lowercaseQuery)) {
          results.push({ variable: trimmedQuery, isNew: true })
        }

        // Sort by variable name in ascending order (case-insensitive)
        return results.sort((a, b) => a.variable.toLowerCase().localeCompare(b.variable.toLowerCase()))
      }

      // Define handlers in renderWithParent scope so they're accessible in both onStart and onUpdate
      const handleModifyVariable = (
        variable: string,
        variableId: string | undefined,
        spaceVariable: SpaceVariable | undefined,
        editor: Editor
      ) => {
        // Always try to get the variable from store to ensure we have the latest data
        const spaceVariableStore = useSpaceVariableStore()
        const spaceId = getScopeId()

        let variableToModify: SpaceVariable | undefined = spaceVariable

        // If spaceVariable is not provided or incomplete, try to find it
        if (!variableToModify || !variableToModify.id) {
          if (variableId && spaceId) {
            // Try to get by ID first (most reliable)
            variableToModify = spaceVariableStore.getVariableById(spaceId, variableId)
          }

          // If still not found, try by name
          if (!variableToModify && spaceId) {
            variableToModify = spaceVariableStore.getVariableByName(spaceId, variable)
          }
        }

        if (variableToModify) {
          // Set flag before hiding popup to prevent create modal from opening
          isProgrammaticHide = true
          openModifyModal(variableToModify, editor)
        } else {
          console.warn('Could not find variable to modify:', { variable, variableId, spaceVariable })
        }
      }

      const handleDeleteVariable = async (
        variable: string,
        variableId: string | undefined,
        spaceVariable: SpaceVariable | undefined,
        editor: Editor
      ) => {
        if (
          !isTemplateContext() &&
          promptUnlockIfVariableSchemaLocked({
            resume: () => reopenVariableActionMenu(editor, variable, variableId, spaceVariable)
          })
        ) {
          dismissSuggestionPopup()
          return
        }

        // Set flag before hiding popup to prevent create modal from opening
        isProgrammaticHide = true

        const spaceVariableStore = useSpaceVariableStore()
        const spaceId = getScopeId()

        let resolvedVariableId: string | undefined = spaceVariable?.id || variableId
        if (!resolvedVariableId && spaceId) {
          const foundVar = spaceVariableStore.getVariableByName(spaceId, variable)
          resolvedVariableId = foundVar?.id
        }

        let didDeleteVariable = false
        if (spaceId && resolvedVariableId) {
          try {
            if (isTemplateContext()) {
              await spaceVariableStore.deleteTemplateVariable(spaceId, resolvedVariableId)
            } else {
              await spaceVariableStore.deleteVariable(spaceId, resolvedVariableId)
            }
            didDeleteVariable = true
          } catch (error) {
            console.error('Failed to delete variable:', error)
            handleVariableDeleteApiError(error, {
              isSpaceScope: !isTemplateContext(),
              onDocumentSigned: () => dismissSuggestionPopup(),
              onRecover: () => reopenVariableActionMenu(editor, variable, resolvedVariableId, spaceVariable)
            })
            return
          }
        }

        if (parent) {
          let root = parent
          while (root.$parent) {
            root = root.$parent
          }
          deleteAllPlaceholderInstancesFromAllEditors(root, resolvedVariableId, variable, editor)
        } else {
          deleteAllPlaceholderInstances(editor, resolvedVariableId, variable, { focusAndSelect: true })
        }

        const remainingInstances = findAllPlaceholderInstances(editor, resolvedVariableId, variable)

        if (!didDeleteVariable && remainingInstances.length === 0 && spaceId) {
          let variableToUpdate: SpaceVariable | undefined = spaceVariable
          if (!variableToUpdate && resolvedVariableId) {
            variableToUpdate = spaceVariableStore.getVariableById(spaceId, resolvedVariableId)
          }
          if (!variableToUpdate) {
            variableToUpdate = spaceVariableStore.getVariableByName(spaceId, variable)
          }

          const appStore = useAppStore()
          const permissionStore = usePermissionStore()
          const isInPreviewOrExternal = appStore.isInPreviewMode || permissionStore.isExternalUser()
          const canUpdateVariable =
            !!editor?.isEditable && !isInPreviewOrExternal && (isTemplateContext() || !isVariableSchemaLocked())
          if (
            canUpdateVariable &&
            variableToUpdate &&
            (variableToUpdate.recipientValue?.trim() || variableToUpdate.assignedToRecipientId)
          ) {
            await spaceVariableStore.updateVariable(spaceId, variableToUpdate.id, {
              variableName: variableToUpdate.variableName,
              displayLabel: variableToUpdate.displayLabel,
              variableType: variableToUpdate.variableType,
              defaultValue: variableToUpdate.defaultValue || undefined,
              dateFormat: variableToUpdate.dateFormat,
              options: variableToUpdate.options
            })
          }
        }

        if (popup?.[0]) {
          popup[0].hide()
        }
        destroyPopup()
        if (component) {
          component.destroy()
        }
      }

      return {
        onStart: (props: any) => {
          range = props.range
          const query = props.query || ''
          const { spaceVariables } = getVariablesFromStore()
          const filtered = filterVariables(spaceVariables, query)
          const items = filtered.map(item => item.variable)

          component = new VueRenderer(VariableList, {
            parent: parent,
            propsData: {
              items,
              query,
              filteredItems: filtered,
              onNewVariable: (variableName: string) => handleNewVariable(variableName, props.editor),
              onModify: (variable: string, variableId?: string, spaceVariable?: SpaceVariable) => {
                handleModifyVariable(variable, variableId, spaceVariable, props.editor)
              },
              onDelete: (variable: string, variableId?: string, spaceVariable?: SpaceVariable) => {
                handleDeleteVariable(variable, variableId, spaceVariable, props.editor)
              },
              onActionMenuOpen: () => {
                // Set flag when action menu opens to prevent popup hide from triggering create modal
                isProgrammaticHide = true
              },
              command: buildCommandHandler(filtered, props.editor)
            }
          })

          // Create onHide callback for blur/click outside
          popup = createPopup(
            props.clientRect,
            component.element as Element,
            props.editor,
            buildHandlePopupHide(props.editor)
          )
        },
        onUpdate(props: any) {
          // Prevent popup recreation during variable insertion
          if (isInserting) {
            return
          }

          // Prevent popup recreation if popup was just destroyed (prevents race condition)
          // This happens when clicking a variable - popup hides, onUpdate runs, but we don't want to recreate
          if (popupJustDestroyed || clickedVariable) {
            return
          }

          range = props.range
          const queryValue = props.query || ''
          const { spaceVariables } = getVariablesFromStore()
          const filtered = filterVariables(spaceVariables, queryValue)

          if (component) {
            component.destroy()
          }

          const items = filtered.map(item => item.variable)
          const filteredItems = filtered.map(item => ({ ...item }))

          // Recreate component with new props instead of updating
          component = new VueRenderer(VariableList, {
            parent: parent,
            propsData: {
              items,
              query: queryValue,
              filteredItems,
              onNewVariable: (variableName: string) => handleNewVariable(variableName, props.editor),
              onModify: (variable: string, variableId?: string, spaceVariable?: SpaceVariable) => {
                handleModifyVariable(variable, variableId, spaceVariable, props.editor)
              },
              onDelete: (variable: string, variableId?: string, spaceVariable?: SpaceVariable) => {
                handleDeleteVariable(variable, variableId, spaceVariable, props.editor)
              },
              onActionMenuOpen: () => {
                // Set flag when action menu opens to prevent popup hide from triggering create modal
                isProgrammaticHide = true
              },
              command: buildCommandHandler(filtered, props.editor)
            }
          })

          // Check if popup exists and is valid (not destroyed)
          // A destroyed instance will have no popper element or the popper won't be in the DOM
          const instance = popup?.[0]
          const popper = instance?.popper
          const isValidPopup = popper && document.body.contains(popper as Node)

          if (isValidPopup) {
            // Update existing valid popup
            try {
              instance.setContent(component.element)
              instance.setProps({
                getReferenceClientRect: anchorRect(props.clientRect, props.editor)
              })
            } catch {
              // If update fails, recreate popup
              destroyPopup()
              popup = createPopup(
                props.clientRect,
                component.element as Element,
                props.editor,
                buildHandlePopupHide(props.editor)
              )
            }
          } else {
            // Recreate popup if it doesn't exist or is invalid
            destroyPopup()
            popup = createPopup(
              props.clientRect,
              component.element as Element,
              props.editor,
              buildHandlePopupHide(props.editor)
            )
          }
        },
        onKeyDown(props: SuggestionKeyDownProps) {
          if (!component || !component.ref) {
            return false
          }

          if (props.event.key === 'Enter') {
            props.event.preventDefault()
            props.event.stopPropagation()
            ;(component.ref as any).onKeyDown(props.event)
            return true
          }

          if (props.event.key === ' ' || props.event.key === 'Space' || props.event.code === 'Space') {
            props.event.preventDefault()
            props.event.stopPropagation()
            lastKeyPressed = 'Space'
            return true
          }

          const hasModifier = props.event.ctrlKey || props.event.metaKey || props.event.altKey
          if (!hasModifier && props.event.key.length === 1 && !isAllowedVariableChar(props.event.key)) {
            props.event.preventDefault()
            props.event.stopPropagation()
            return true
          }

          lastKeyPressed = props.event.key

          const handled = (component.ref as any).onKeyDown(props.event)

          if (handled) {
            props.event.preventDefault()
            props.event.stopPropagation()
            return true
          }

          return false
        },
        onExit(_props: any) {
          // Don't process if this is a programmatic hide (user clicked a variable)
          // Also check clickedVariable to handle cases where flag was reset but variable was clicked
          if (isProgrammaticHide || clickedVariable) {
            destroyPopup()
            if (component) {
              component.destroy()
            }
            isProgrammaticHide = false
            clickedVariable = null
            return
          }

          if (lastKeyPressed === ' ' || lastKeyPressed === 'Space') {
            lastKeyPressed = null
            return
          }

          // Don't open modal from onExit - let handlePopupHide handle it
          // onExit runs before command callback, causing race conditions
          // The handlePopupHide callback will handle opening modal on blur/click outside

          destroyPopup()
          if (component) {
            component.destroy()
          }
        }
      }
    }

    return [
      Suggestion({
        editor: editor,
        pluginKey: variableSuggestionPluginKey,
        char: '',
        startOfLine: false,
        allowedPrefixes: null,
        allowSpaces: false,
        decorationTag: 'span',
        decorationClass: 'variable-suggestion-decoration',
        findSuggestionMatch: findVariableSuggestionMatch,
        items: this.options.suggestion.items,
        render: renderWithParent,
        command: this.options.suggestion.command
      }),
      new Plugin({
        key: new PluginKey('variableSuggestionPaste'),
        props: {
          handlePaste: (view, event) => {
            if (!editor?.isEditable) {
              return false
            }

            const pastedText = event.clipboardData?.getData('text/plain')
            if (!pastedText) {
              return false
            }

            const { selection } = view.state
            const positions = findVariablePositions({ from: selection.from, to: selection.to }, editor)
            if (!positions) {
              return false
            }

            const sanitizedText = sanitizeVariableName(pastedText)
            if (!sanitizedText) {
              return true
            }

            const tr = view.state.tr.insertText(sanitizedText, selection.from, selection.to)
            view.dispatch(tr.scrollIntoView())
            return true
          }
        }
      })
    ]
  }
})

export function openVariableSuggestionAtView(view: EditorView, selectionPos: number): void {
  const { state } = view
  if (!variableSuggestionPluginKey.getState(state)) {
    return
  }

  const docSize = state.doc.content.size
  if (selectionPos < 0 || selectionPos > docSize) {
    return
  }

  view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, selectionPos)))

  // Trigger suggestion plugin check on the next frame
  requestAnimationFrame(() => {
    const nextState = view.state
    view.dispatch(nextState.tr.setSelection(nextState.selection))
  })
}

export function openVariableSuggestionAt(editor: Editor, selectionPos: number): void {
  const view = editor?.view
  if (!view) {
    return
  }
  openVariableSuggestionAtView(view, selectionPos)
}
